import { Component, OnInit, ChangeDetectorRef, ViewChild, HostListener, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { Header } from './header/header';
import { Sidebar } from './sidebar/sidebar';
import { Footer } from './footer/footer';
import { LayoutService } from '../core/services/layout/layout.service';
import { NavigationItem } from '../core/services/sidebar/sidebar.service';
import { RightSidebarComponent } from './right-sidebar/right-sidebar';
import { ChangePasswordFormComponent } from '../shared/components/change-password-form/change-password-form';
import { ThemeSelectionModalComponent } from '../shared/components/theme-selection-modal/theme-selection-modal.component';
import { MySettings, UserSettings } from '../shared/components/my-settings/my-settings';
import { NotificationService, Notification, PopupNotification } from '../core/services/notifications/notification.service';
import { MetaService } from '../core/services/meta/meta.service';
import { AuthService } from '../core/services/Auth/auth-service';
import { TimesheetStatusService } from '../core/services/timesheet-status/timesheet-status.service';
import { NavigationService } from '../core/services/sidebar/sidebar.service';
import { filter, take } from 'rxjs/operators';
import { MatIconModule } from '@angular/material/icon';
import { ToastrService } from 'ngx-toastr';
import { Spinner } from '@app/shared/components/spinner/spinner';
import { AiHelpAssistantPanel } from '@app/features/ai-help-assistant';
import { matTooltipSmartImports } from '@app/shared/imports/mat-tooltip-smart';
import { TimesheetSubmissionRequiredPopup } from '@app/shared/components/timesheet-submission-required-popup/timesheet-submission-required-popup';
import { FestivalDecoration, FestivalType, getActiveFestivalType } from '@app/shared/components/festival-decoration/festival-decoration';
import { BodyScrollLockService } from '@app/core/services/body-scroll-lock/body-scroll-lock.service';
import { NotificationAttachmentPreviewService } from '@app/core/services/notification-attachment-preview/notification-attachment-preview.service';
import { ImagePreviewModalComponent } from '@app/shared/components/image-preview-modal/image-preview-modal';
import { MediaPreviewModalComponent } from '@app/shared/components/media-preview-modal/media-preview-modal';

/** Only members of wyseguys see  profile dropdown. */
const SETUP_LINKS_HOME_TEAM_ID = '5a7a10a6001139dc1f84b76c96b1d7be';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.html',
  styleUrls: ['./layout.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    Header,
    Sidebar,
    Footer,
    RightSidebarComponent,
    ChangePasswordFormComponent,
    ThemeSelectionModalComponent,
    MySettings,
    MatIconModule,
    Spinner,
    AiHelpAssistantPanel,
    TimesheetSubmissionRequiredPopup,
    FestivalDecoration,
    ImagePreviewModalComponent,
    MediaPreviewModalComponent,
    ...matTooltipSmartImports,
  ]
})
export class Layout implements OnInit {
  @ViewChild(Sidebar) sidebarComponent!: Sidebar;
  @ViewChild('headerComp') headerComponent!: Header;

  private static readonly THANKSGIVING_BANNER_DISMISS_KEY = 'wl-thanksgiving-banner-dismissed';
  private static readonly HALLOWEEN_BANNER_DISMISS_KEY = 'wl-halloween-banner-dismissed';
  private static readonly FALL_BANNER_DISMISS_KEY = 'wl-fall-banner-dismissed';
  private static readonly REMEMBRANCE_BANNER_DISMISS_KEY = 'wl-remembrance-banner-dismissed';

  readonly thanksgivingBannerVisible = signal(
    getActiveFestivalType() === FestivalType.THANKSGIVING &&
      !Layout.isThanksgivingBannerDismissed()
  );
  readonly halloweenBannerVisible = signal(
    getActiveFestivalType() === FestivalType.HALLOWEEN &&
      !Layout.isHalloweenBannerDismissed()
  );
  readonly fallBannerVisible = signal(
    getActiveFestivalType() === FestivalType.AUTUMN &&
      !Layout.isFallBannerDismissed()
  );
  readonly remembranceBannerVisible = signal(
    getActiveFestivalType() === FestivalType.REMEMBRANCE &&
      !Layout.isRemembranceBannerDismissed()
  );
  readonly thanksgivingUserName = computed(() => {
    const fullName = String(this.authService.userData()?.name || '').trim();
    if (!fullName) {
      return 'there';
    }
    return fullName.split(/\s+/)[0];
  });
  readonly thanksgivingBannerLeaves = [
    { src: 'assets/images/events/maple1.png', left: '6%', size: '18px', duration: '4.2s', delay: '0s', drift: '12px', spin: '220deg' },
    { src: 'assets/images/events/maple2.png', left: '18%', size: '16px', duration: '4.8s', delay: '0.5s', drift: '-14px', spin: '-190deg' },
    { src: 'assets/images/events/maple3.png', left: '32%', size: '20px', duration: '4.4s', delay: '1s', drift: '10px', spin: '250deg' },
    { src: 'assets/images/events/head-maple.png', left: '46%', size: '17px', duration: '5s', delay: '0.2s', drift: '-8px', spin: '-170deg' },
    { src: 'assets/images/events/maple1.png', left: '60%', size: '19px', duration: '4.6s', delay: '0.8s', drift: '14px', spin: '200deg' },
    { src: 'assets/images/events/maple2.png', left: '74%', size: '16px', duration: '5.1s', delay: '1.3s', drift: '-11px', spin: '-210deg' },
    { src: 'assets/images/events/maple3.png', left: '88%', size: '18px', duration: '4.7s', delay: '1.6s', drift: '9px', spin: '180deg' },
  ];
  readonly halloweenBannerBats = [
    { left: '62%', top: '12%', size: '18px', duration: '5.2s', delay: '0s', path: 'a' },
    { left: '74%', top: '28%', size: '15px', duration: '6s', delay: '0.5s', path: 'b' },
    { left: '84%', top: '10%', size: '17px', duration: '5.6s', delay: '1.1s', path: 'c' },
  ];
  readonly remembranceBannerPoppies = [
    { src: 'assets/images/events/rem-fl.png', left: '8%', size: '10px', duration: '4.4s', delay: '0s', drift: '10px', spin: '180deg' },
    { src: 'assets/images/events/remember-fl.png', left: '22%', size: '11px', duration: '5s', delay: '0.6s', drift: '-12px', spin: '-160deg' },
    { src: 'assets/images/events/rem-fl.png', left: '38%', size: '9px', duration: '4.6s', delay: '1.1s', drift: '8px', spin: '200deg' },
    { src: 'assets/images/events/remember-fl.png', left: '54%', size: '10px', duration: '5.2s', delay: '0.3s', drift: '-9px', spin: '-190deg' },
    { src: 'assets/images/events/rem-fl.png', left: '70%', size: '11px', duration: '4.8s', delay: '1.4s', drift: '12px', spin: '170deg' },
    { src: 'assets/images/events/remember-fl.png', left: '86%', size: '9px', duration: '5.1s', delay: '0.9s', drift: '-8px', spin: '-210deg' },
  ];

  readonly timesheetSubmissionPopupShow = computed(
    () => this.timesheetStatusService.submissionPopupOpen()
  );
  readonly timesheetSubmissionPopupMessage = computed(() =>
    this.timesheetStatusService.warningMessage()
  );
  readonly timesheetSubmissionPopupHours = computed(() =>
    this.timesheetStatusService.filledHoursForPopup()
  );

  activeHeader: NavigationItem | null = null;
  isChangePasswordOpen = false;
  themeSelectionModalOpen = false;
  showSettingsSlider = false;
  profileDropdownOpen = false;
  /** Master Approvals / Request Queues links in the profile dropdown (home team restricted). */
  canSeeSetupLinks = false;
  profileDropdownUserName: string | null = null;
  profileDropdownUserImage: string | null = null;
  private _isOnProjectDetailsPage: boolean = false;
  profileDropdownUserId: string | null = null;
  switchBackLoading = false;
  logoutLoading = false;
  readonly isImpersonating = computed(() => this.authService.impersonating());
  readonly passwordResetRequired = computed(() => this.authService.passwordResetRequired());
  readonly originalAccountName = computed(
    () => this.authService.getImpersonationOriginalUserName() || 'My Account'
  );
  readonly impersonatedUserName = computed(
    () => this.authService.userData()?.name || 'another user'
  );
  /** Recreate the routed page when login-as / switch-back changes the session. */
  readonly pageSessionKey = computed(() => this.authService.sessionViewKey());
  readonly impersonationSwitchBackTooltip = computed(() => {
    if (this.switchBackLoading) {
      return 'Switching...';
    }
    const name = this.authService.getImpersonationOriginalUserName() || 'My Account';
    return `Back to ${name}`;
  });
  
  // Mobile radial menu properties
  isMobileRadialMenuOpen = false;
  mobileRadialMenuItems = [
    {
      id: 'timesheets',
      label: 'Timesheets',
      icon: 'assets/images/timesheet.png',
      route: '/timesheets'
    },
    {
      id: 'projects-all',
      label: 'Projects',
      icon: 'assets/images/all-projects.png',
      route: '/projects/projects-all'
    },
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: 'assets/images/dashboard.png',
      route: '/dashboard'
    },
    {
      id: 'requests',
      label: 'Requests',
      icon: 'assets/images/request.png',
      route: '/requests'
    },
    {
      id: 'clients',
      label: 'Clients',
      icon: 'assets/images/clients.png',
      route: '/clients-list'
    }
  ];
  // showClientSummarySlider = false;

  userInfoBg: string = '';
  private banners: string[] = [
    'assets/images/theme-banner-dark.png',
    'assets/images/theme-banner-ocean.png',
    'assets/images/theme-banner.png'
  ];
  private bannerIndex: number = 2;

  // Store original state for preview functionality
  private originalBackgroundImage: string = '';
  private originalUserInfoBg: string = '';
  private originalThemeState: boolean = false;

  // Add properties to track cleanup
  private backgroundUpdateInterval: any;
  private mutationObserver: MutationObserver | null = null;
  private storageEventListener: ((event: StorageEvent) => void) | null = null;
  private lastLoggedTheme: string = '';
  private mutationTimeout: any = null;
  private routeChangeTimeout: any = null;
  
  // Add flag to prevent infinite loops in theme consistency checking
  private isThemeConsistencyCheckInProgress: boolean = false;
  private lastThemeClearLog: number = 0;
  private lastLoggedBackground: string = '';
  /** Used so we only clear notification popups when leaving the dashboard, not on every in-app navigation. */
  private previousUrlAfterRedirects = '';

  /** Dashboard routes where mobile (≤767) should hide the right sidebar. */
  private readonly isDashboardRoute = signal(false);
  private readonly isMobileViewport = signal(false);

  // Notification service signals - use computed for proper reactivity
  // Only show popup notifications on dashboard page
  popupNotifications = computed(() => {
    // const notifications = this.notificationService.popupNotifications();
    // // Filter to only show on dashboard
    // const isOnDashboard = /^\/dashboard/.test(this.router.url);
    // return isOnDashboard ? notifications : [];
    return this.notificationService.popupNotifications();
  });

  /** Precompute popup display fields once when the popup list changes (avoids template getter thrash). */
  popupViews = computed(() => this.popupNotifications().map((popup) => this.buildPopupView(popup)));

  private readonly popupDateTimeFormatter = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  
  // Track image loading errors
  imageLoadError: { [key: string]: boolean } = {};

  // Popup dropdown state
  popupDropdownOpen = signal<{ [key: string]: boolean }>({});

  constructor(
    public layoutService: LayoutService,
    public router: Router,
    private cdr: ChangeDetectorRef,
    public notificationService: NotificationService,
    private metaService: MetaService,
    private authService: AuthService,
    private navigationService: NavigationService,
    private toastr: ToastrService,
    public timesheetStatusService: TimesheetStatusService,
    private bodyScrollLockService: BodyScrollLockService,
    public notificationAttachmentPreview: NotificationAttachmentPreviewService
  ) {
    // No need to initialize here since we're using computed
  }

  dismissThanksgivingBanner(): void {
    try {
      localStorage.setItem(Layout.thanksgivingBannerStorageKey(), '1');
    } catch {
      // ignore storage failures
    }
    this.thanksgivingBannerVisible.set(false);
  }

  dismissHalloweenBanner(): void {
    try {
      localStorage.setItem(Layout.halloweenBannerStorageKey(), '1');
    } catch {
      // ignore storage failures
    }
    this.halloweenBannerVisible.set(false);
  }

  dismissFallBanner(): void {
    try {
      localStorage.setItem(Layout.fallBannerStorageKey(), '1');
    } catch {
      // ignore storage failures
    }
    this.fallBannerVisible.set(false);
  }

  dismissRemembranceBanner(): void {
    try {
      localStorage.setItem(Layout.remembranceBannerStorageKey(), '1');
    } catch {
      // ignore storage failures
    }
    this.remembranceBannerVisible.set(false);
  }

  private static isThanksgivingBannerDismissed(): boolean {
    try {
      return localStorage.getItem(Layout.thanksgivingBannerStorageKey()) === '1';
    } catch {
      return false;
    }
  }

  private static isHalloweenBannerDismissed(): boolean {
    try {
      return localStorage.getItem(Layout.halloweenBannerStorageKey()) === '1';
    } catch {
      return false;
    }
  }

  private static thanksgivingBannerStorageKey(): string {
    return `${Layout.THANKSGIVING_BANNER_DISMISS_KEY}-${new Date().getFullYear()}`;
  }

  private static halloweenBannerStorageKey(): string {
    return `${Layout.HALLOWEEN_BANNER_DISMISS_KEY}-${new Date().getFullYear()}`;
  }

  private static isFallBannerDismissed(): boolean {
    try {
      return localStorage.getItem(Layout.fallBannerStorageKey()) === '1';
    } catch {
      return false;
    }
  }

  private static isRemembranceBannerDismissed(): boolean {
    try {
      return localStorage.getItem(Layout.remembranceBannerStorageKey()) === '1';
    } catch {
      return false;
    }
  }

  private static fallBannerStorageKey(): string {
    return `${Layout.FALL_BANNER_DISMISS_KEY}-${new Date().getFullYear()}`;
  }

  private static remembranceBannerStorageKey(): string {
    return `${Layout.REMEMBRANCE_BANNER_DISMISS_KEY}-${new Date().getFullYear()}`;
  }

  ngOnInit() {
    this.bodyScrollLockService.startWatching();
    this.enforceTimesheetSubmissionOnLoad();
    // Initialize user info background with time-based image
    this.userInfoBg = this.getTimeBasedBackground();
    this.updateDashboardRouteFlag(this.router.url);
    this.updateMobileViewportFlag();

    this.layoutService.getActiveHeader().subscribe(header => {
      this.activeHeader = header;
    });

    // Listen to route changes to detect project-details page and handle theme application
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.checkIfOnProjectDetailsPage();
      const url = event.urlAfterRedirects;
      this.updateDashboardRouteFlag(url);
      const isOnDashboard = this.isDashboardRoute();
      const wasOnDashboard = /^\/(dashboard|dashboard-new|single-dashboard)/.test(this.previousUrlAfterRedirects);
      this.previousUrlAfterRedirects = url;

      // Only dismiss popups when actually leaving the dashboard. Clearing on every non-dashboard
      // NavigationEnd removed task-assignment toasts while navigating tasks/requests/revisions.
      if (wasOnDashboard && !isOnDashboard) {
        const currentPopups = this.notificationService.popupNotifications();
        currentPopups.forEach(popup => {
          this.notificationService.dismissPopup(popup.id);
        });

        this.layoutService.rightSidebarContent$.pipe(take(1)).subscribe(content => {
          if (content === 'notifications') {
            this.layoutService.setRightSidebarContent(null);
          }
        });
      }

      // Ensure theme is applied consistently after navigation (with throttling)
      // (excluding login page which is handled separately)
      if (this.routeChangeTimeout) {
        clearTimeout(this.routeChangeTimeout);
      }
      this.routeChangeTimeout = setTimeout(() => {
        this.ensureThemeConsistency();
      }, 200);
    });

    // Check initial route
    this.checkIfOnProjectDetailsPage();

    // Load saved background theme on init - this ensures theme is restored after login
    this.loadSavedBackgroundTheme();

    // Set up periodic background updates (every 30 minutes)
    this.setupPeriodicBackgroundUpdate();

    // Set up theme consistency monitoring
    this.setupThemeConsistencyMonitoring();
  }

  private enforceTimesheetSubmissionOnLoad(): void {
    if (!this.authService.isLoggedIn() && !this.timesheetStatusService.hasAuthSession()) {
      return;
    }

    this.timesheetStatusService.ensureStatus().pipe(take(1)).subscribe((status) => {
      if (status.success || this.timesheetStatusService.isTimesheetRoute(this.router.url)) {
        if (!status.success && this.timesheetStatusService.isTimesheetRoute(this.router.url)) {
          this.timesheetStatusService.openSubmissionWarning();
        }
        return;
      }

      const message = status.message || 'Please submit your timesheet.';
      // this.toastr.warning(message, '', { timeOut: 6000 });
      this.timesheetStatusService.openSubmissionWarning();
      void this.router.navigate(['/timesheets'], { replaceUrl: true });
    });
  }

  onTimesheetSubmissionPopupConfirm(): void {
    this.timesheetStatusService.closeSubmissionPopup();
    this.timesheetStatusService.requestOpenPendingWeek();

    void this.router.navigate(['/timesheets'], {
      queryParams: { tab: 'timesheet' },
    });
  }

  onTimesheetSubmissionPopupCancel(): void {
    this.timesheetStatusService.closeSubmissionPopup();
    if (!this.timesheetStatusService.isTimesheetRoute(this.router.url)) {
      void this.router.navigate(['/timesheets'], { replaceUrl: true });
    }
  }

  private checkIfOnProjectDetailsPage(): void {
    const currentUrl = this.router.url;
    // Check if URL matches pattern: projects/project-details/{id}
    this._isOnProjectDetailsPage = /^\/projects\/project-details\/[^\/]+$/.test(currentUrl);
  }

  isOnProjectDetailsPage(): boolean {
    return this._isOnProjectDetailsPage;
  }

  // onClientSummaryClick(): void {
  //   this.showClientSummarySlider = true;
  // }

  // onCloseClientSummarySlider(): void {
  //   this.showClientSummarySlider = false;
  // }

  toggleSidebar() {
    this.layoutService.toggleSidebar();
  }

  cycleUserInfoBg() {
    this.bannerIndex = (this.bannerIndex + 1) % this.banners.length;
    this.userInfoBg = this.banners[this.bannerIndex];
  }

  onChangePasswordPanelToggled(event: any) {
    // Block dismissing the form while password reset is required
    if (!event && this.authService.passwordResetRequired()) {
      this.isChangePasswordOpen = true;
      return;
    }

    if (event && this.logoutLoading) {
      return;
    }

    this.isChangePasswordOpen = event;
    // Close profile dropdown when opening change password form
    if (event) {
      this.profileDropdownOpen = false;
    }
  }

  onBackgroundImageSelected(imagePath: string) {
    if (imagePath === '') {
      // Clear theme and restore original backgrounds consistently
      this.clearThemeConsistently();
      // Also clear from localStorage
      localStorage.removeItem('selectedBackgroundTheme');
      localStorage.removeItem('theme-background-active');
    } else {
      // Apply theme consistently to both body and user_info
      this.applyThemeConsistently(imagePath);
      // Save to localStorage
      localStorage.setItem('selectedBackgroundTheme', imagePath);
      localStorage.setItem('theme-background-active', 'true');
    }

    // Clear original state as changes are now permanent
    this.resetOriginalState();
  }

  onBackgroundImagePreview(imagePath: string) {
    // Store original state before first preview
    if (!this.originalBackgroundImage) {
      this.originalBackgroundImage = document.body.style.backgroundImage;
      this.originalUserInfoBg = this.userInfoBg;
      this.originalThemeState = this.isThemeActive();
    }

    if (imagePath === '') {
      // Preview clear theme - show time-based background consistently
      this.clearThemeConsistently();
    } else {
      // Preview theme - apply temporarily but consistently
      this.applyThemeConsistently(imagePath);
    }

    // Ensure change detection is triggered
    this.cdr.detectChanges();
  }

  private applyBackgroundToLayout(imagePath: string) {
    // Apply background image to the body with overlay
    const body = document.body;
    body.style.backgroundImage = `url(${imagePath})`;
    body.style.backgroundSize = 'cover';
    body.style.backgroundPosition = 'center';
    body.style.backgroundRepeat = 'no-repeat';
    body.style.backgroundAttachment = 'fixed';

    // Add overlay class to body
    body.classList.add('theme-background-active');

    // Save to localStorage
    localStorage.setItem('selectedBackgroundTheme', imagePath);
    localStorage.setItem('theme-background-active', 'true');
  }

  private applyBackgroundToUserInfo(imagePath: string) {
    // Save the current time-based background as the original
    if (!localStorage.getItem('originalUserInfoBg')) {
      localStorage.setItem('originalUserInfoBg', this.getTimeBasedBackground());
    }

    // Update user-info background with the selected theme
    this.userInfoBg = imagePath;

    // Trigger change detection to update the view immediately
    this.cdr.detectChanges();
  }

  private loadSavedBackgroundTheme() {
    const savedTheme = localStorage.getItem('selectedBackgroundTheme');
    const wasThemeActive = localStorage.getItem('theme-background-active') === 'true';

    if (savedTheme && wasThemeActive) {
      // Use the new consistent theme application method
      this.applyThemeConsistently(savedTheme);
    } else {
      // Use the new consistent theme clearing method
      this.clearThemeConsistently();
    }
  }

  clearBackgroundTheme() {
    this.clearThemeConsistently();
    // Also clear from localStorage
    localStorage.removeItem('selectedBackgroundTheme');
    localStorage.removeItem('theme-background-active');
  }

  private restoreOriginalUserInfoBackground() {
    // Always use the current time-based background when theme is disabled
    this.userInfoBg = this.getTimeBasedBackground();
    localStorage.removeItem('originalUserInfoBg');

    // Trigger change detection to update the view immediately
    this.cdr.detectChanges();

    // Force sidebar to refresh its background with time-based image
    setTimeout(() => {
      this.refreshSidebarBackground();

      // Additional refresh to ensure the sidebar gets the updated background
      if (this.sidebarComponent) {
        this.sidebarComponent.forceRefreshTimeBasedBackground();
      }
    }, 0);

    // Final delayed update to ensure visual changes are applied
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 150);
  }

  private getTimeBasedBackground(): string {
    const currentHour = new Date().getHours();
    let background = '';
    let timePeriod = '';

    // Define time periods and their corresponding backgrounds
    if (currentHour >= 5 && currentHour < 12) {
      // Morning (5 AM - 12 PM) - Light theme banner
      background = 'assets/images/theme-banner.png';
      timePeriod = 'morning';
    } else if (currentHour >= 12 && currentHour < 17) {
      // Afternoon (12 PM - 5 PM) - Ocean theme banner
      background = 'assets/images/theme-banner-ocean.png';
      timePeriod = 'afternoon';
    } else if (currentHour >= 17 && currentHour < 21) {
      // Evening (5 PM - 9 PM) - Dark theme banner
      background = 'assets/images/theme-banner-dark.png';
      timePeriod = 'evening';
    } else {
      // Night (9 PM - 5 AM) - Dark theme banner
      background = 'assets/images/theme-banner-dark.png';
      timePeriod = 'night';
    }

    return background;
  }

  private setupPeriodicBackgroundUpdate() {
    // Update background every 30 minutes if no theme is active
    this.backgroundUpdateInterval = setInterval(() => {
      if (!this.isThemeActive()) {
        const newTimeBasedBg = this.getTimeBasedBackground();
        if (this.userInfoBg !== newTimeBasedBg) {
          this.userInfoBg = newTimeBasedBg;
          // Trigger change detection to update the view immediately
          this.cdr.detectChanges();
        }
      }
    }, 30 * 60 * 1000); // 30 minutes in milliseconds
  }

  private isThemeActive(): boolean {
    return localStorage.getItem('theme-background-active') === 'true';
  }

  public refreshUserInfoBackground() {
    // Force refresh the user info background to current time-based image
    if (!this.isThemeActive()) {
      this.userInfoBg = this.getTimeBasedBackground();
      this.cdr.detectChanges();
    }
  }

  private refreshSidebarBackground() {
    // Force sidebar to use current time-based background when theme is disabled
    if (!this.isThemeActive()) {
      this.userInfoBg = this.getTimeBasedBackground();
      this.cdr.detectChanges();

      // If sidebar component is available, force refresh its background directly
      if (this.sidebarComponent) {
        this.sidebarComponent.forceRefreshTimeBasedBackground();
      }

      // Additional force refresh after a short delay to ensure proper rendering
      setTimeout(() => {
        this.cdr.detectChanges();
      }, 50);
    }
  }

  private previewApplyTheme(imagePath: string) {
    // Apply theme preview to body background
    const body = document.body;
    body.style.backgroundImage = `url(${imagePath})`;
    body.style.backgroundSize = 'cover';
    body.style.backgroundPosition = 'center';
    body.style.backgroundRepeat = 'no-repeat';
    body.style.backgroundAttachment = 'fixed';

    // Add theme class for overlay effects
    body.classList.add('theme-background-active');

    // Update user info background with preview
    this.userInfoBg = imagePath;

    // Force sidebar to update with preview
    if (this.sidebarComponent) {
      this.sidebarComponent.userInfoBg = imagePath;
      this.sidebarComponent.refreshUserInfoBackground();
    }
  }

  private previewClearTheme() {
    // Clear preview theme from body
    const body = document.body;
    body.style.backgroundImage = '';
    body.classList.remove('theme-background-active');

    // Restore time-based background for preview
    this.userInfoBg = this.getTimeBasedBackground();

    // Force sidebar to update with time-based background
    if (this.sidebarComponent) {
      this.sidebarComponent.forceRefreshTimeBasedBackground();
    }
  }

  private resetOriginalState() {
    // Clear stored original state after permanent changes
    this.originalBackgroundImage = '';
    this.originalUserInfoBg = '';
    this.originalThemeState = false;
  }

  showRightSidebar(): boolean {
    return this.shouldShowRightSidebar();
  }

  /** Visible when layout service enables it, except on dashboard at ≤767px. */
  readonly shouldShowRightSidebar = computed(() => {
    if (this.isDashboardRoute() && this.isMobileViewport()) {
      return false;
    }
    return this.layoutService.showRightSidebar();
  });

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateMobileViewportFlag();
  }

  private updateDashboardRouteFlag(url: string): void {
    const path = (url || '').split('?')[0].split('#')[0];
    this.isDashboardRoute.set(
      path === '/' || /^\/(dashboard|dashboard-new|single-dashboard)(\/|$)/.test(path)
    );
  }

  private updateMobileViewportFlag(): void {
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 767;
    if (this.isMobileViewport() !== isMobile) {
      this.isMobileViewport.set(isMobile);
    }
  }

  private ensureThemeConsistency() {
    // Prevent infinite loops by checking if already in progress
    if (this.isThemeConsistencyCheckInProgress) {
      return;
    }

    // Skip theme application if on login page (though layout shouldn't be used for login)
    const currentRoute = this.router.url;
    if (currentRoute.includes('/login') || currentRoute.includes('/forgotpassword') || currentRoute.includes('/login-with-otp')) {
      return;
    }

    this.isThemeConsistencyCheckInProgress = true;

    try {
      // Check if theme should be active
      const savedTheme = localStorage.getItem('selectedBackgroundTheme');
      const isThemeActive = localStorage.getItem('theme-background-active') === 'true';

      if (savedTheme && isThemeActive) {
        // Ensure both body and user_info have the theme applied
        this.applyThemeConsistently(savedTheme);
      } else {
        // Ensure no theme is applied and time-based backgrounds are used
        this.clearThemeConsistently();
      }
    } finally {
      // Always reset the flag, even if an error occurs
      this.isThemeConsistencyCheckInProgress = false;
    }
  }

  private setupThemeConsistencyMonitoring() {
    // Monitor for changes in theme-background-active class
    this.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const hasThemeClass = document.body.classList.contains('theme-background-active');
          const themeActiveInStorage = localStorage.getItem('theme-background-active') === 'true';

          // If there's a mismatch, synchronize (with throttling)
          if (hasThemeClass !== themeActiveInStorage) {
            // Clear existing timeout
            if (this.mutationTimeout) {
              clearTimeout(this.mutationTimeout);
            }
            
            // Throttle the consistency check to prevent excessive calls
            this.mutationTimeout = setTimeout(() => {
              this.ensureThemeConsistency();
            }, 100);
          }
        }
      });
    });

    // Start observing body class changes
    this.mutationObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    // Also monitor localStorage changes (for cross-tab synchronization)
    this.storageEventListener = (event) => {
      if (event.key === 'theme-background-active' || event.key === 'selectedBackgroundTheme') {
        // Add small delay to prevent immediate re-triggering
        setTimeout(() => {
          this.ensureThemeConsistency();
        }, 50);
      }
    };
    window.addEventListener('storage', this.storageEventListener);
  }

  private applyThemeConsistently(imagePath: string) {
    // Apply to body background
    const body = document.body;
    body.style.backgroundImage = `url(${imagePath})`;
    body.style.backgroundSize = 'cover';
    body.style.backgroundPosition = 'center';
    body.style.backgroundRepeat = 'no-repeat';
    body.style.backgroundAttachment = 'fixed';
    body.classList.add('theme-background-active');

    // Apply to user_info background
    this.userInfoBg = imagePath;

    // Force sidebar to update with the same theme
    if (this.sidebarComponent) {
      this.sidebarComponent.userInfoBg = imagePath;
      this.sidebarComponent.refreshUserInfoBackground();
    }

    // Trigger change detection
    this.cdr.detectChanges();

    // Only log if the theme is different from the last logged theme
    if (this.lastLoggedTheme !== imagePath) {
      this.lastLoggedTheme = imagePath;
    }
  }

  private clearThemeConsistently() {
    // Clear body background
    const body = document.body;
    body.style.backgroundImage = '';
    body.classList.remove('theme-background-active');

    // Restore time-based background for user_info
    this.userInfoBg = this.getTimeBasedBackground();

    // Force sidebar to update with time-based background
    if (this.sidebarComponent) {
      this.sidebarComponent.forceRefreshTimeBasedBackground();
    }

    // Trigger change detection
    this.cdr.detectChanges();

    // Keep tracking fields in sync to avoid redundant recalculation paths.
    this.lastThemeClearLog = Date.now();
    this.lastLoggedBackground = this.userInfoBg;
  }

  // Theme Selection Modal Methods
  onThemeBtnClick() {
    if (this.logoutLoading) {
      return;
    }
    this.themeSelectionModalOpen = true;
    // Close profile dropdown when opening theme modal
    this.profileDropdownOpen = false;
    this.cdr.detectChanges();
  }

  onCloseThemeSelectionModal() {
    this.themeSelectionModalOpen = false;
    this.cdr.detectChanges();
  }

  onThemeSelected(bannerPath: string) {
    this.onBackgroundImageSelected(bannerPath);
    this.onCloseThemeSelectionModal();
  }

  onThemePreview(bannerPath: string) {
    this.onBackgroundImagePreview(bannerPath);
  }

  onThemeToggled(isActive: boolean) {
    if (isActive) {
      // If turning on, apply saved theme or default theme via preview
      const savedTheme = localStorage.getItem('selectedBackgroundTheme');
      if (savedTheme) {
        // Preview saved theme
        this.onBackgroundImagePreview(savedTheme);
      } else {
        // Preview default theme (banner1.webp)
        const defaultTheme = 'assets/images/theme/banner1.webp';
        this.onBackgroundImagePreview(defaultTheme);
      }
    } else {
      // If turning off, emit clear signal to show time-based background via preview
      this.onBackgroundImagePreview('');

      // Force change detection to ensure UI updates immediately
      this.cdr.detectChanges();
    }

    // Note: Modal remains open, theme changes are applied as preview
    // Permanent changes only happen when user clicks Save
  }

  ngOnDestroy() {
    this.bodyScrollLockService.stopWatching();

    // Clean up interval
    if (this.backgroundUpdateInterval) {
      clearInterval(this.backgroundUpdateInterval);
    }

    // Clean up mutation timeout
    if (this.mutationTimeout) {
      clearTimeout(this.mutationTimeout);
    }

    // Clean up route change timeout
    if (this.routeChangeTimeout) {
      clearTimeout(this.routeChangeTimeout);
    }

    // Clean up mutation observer
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }

    // Clean up storage event listener
    if (this.storageEventListener) {
      window.removeEventListener('storage', this.storageEventListener);
    }
  }

  // Settings slider methods
  onSettingsClick(): void {
    if (this.logoutLoading) {
      return;
    }
    this.showSettingsSlider = true;
    // Close profile dropdown when opening settings
    this.profileDropdownOpen = false;
  }

  /** Setup pages linked from the profile dropdown (master approvals, request queues). */
  navigateFromProfileDropdown(url: string): void {
    if (this.logoutLoading) {
      return;
    }
    this.profileDropdownOpen = false;
    void this.router.navigateByUrl(url);
  }

  onCloseSettingsSlider(): void {
    this.showSettingsSlider = false;
  }


 // Profile dropdown methods
 onProfileDropdownStateChange(state: { isOpen: boolean, userName: string | null, userImage: string | null }): void {
  // Keep dropdown open while sign-out API is in progress
  if (this.logoutLoading && !state.isOpen) {
    return;
  }
  this.profileDropdownOpen = state.isOpen;
  this.profileDropdownUserName = state.userName;
  this.profileDropdownUserImage = state.userImage;
  // Get userId from localStorage
  this.profileDropdownUserId = localStorage.getItem('userID');
  this.canSeeSetupLinks =
    (localStorage.getItem('homeTeamID') ?? '').trim() === SETUP_LINKS_HOME_TEAM_ID;
  this.cdr.detectChanges();
}

// Navigate to profile view page
navigateToProfileView(event?: Event): void {
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }
  if (this.logoutLoading) {
    return;
  }
  if (this.profileDropdownUserId) {
    this.profileDropdownOpen = false;
    this.router.navigate(['/people/peopleview', this.profileDropdownUserId]);
  }
}

  onLogoutClick(event?: Event): void {
    event?.stopPropagation();
    if (this.logoutLoading) {
      return;
    }
    // Keep dropdown open and show spinner until logout API completes
    if (this.headerComponent) {
      this.headerComponent.logout();
    }
  }

  onLogoutLoadingChange(loading: boolean): void {
    this.logoutLoading = loading;
    if (!loading) {
      this.profileDropdownOpen = false;
    }
    this.cdr.detectChanges();
  }

  onSwitchBackToOriginalAccount(): void {
    if (this.headerComponent) {
      this.headerComponent.onSwitchBackToOriginalAccount();
    }
  }

  onAccountSwitchedBack(): void {
    if (this.sidebarComponent) {
      this.sidebarComponent.refreshUserDisplay();
    }
    this.profileDropdownOpen = false;
    this.cdr.detectChanges();
  }

  onSettingsSaved(settings: UserSettings): void {
    // Forward the settings to the header component
    if (this.headerComponent) {
      this.headerComponent.onSettingsSaved(settings);
      // Also refresh user image from localStorage as a fallback with a small delay
      setTimeout(() => {
        this.headerComponent.refreshUserImage();
      }, 100);
    }
    // Handle settings saved event if needed
  }

  // Mobile radial menu methods
  onMobileAddClick(): void {
    this.isMobileRadialMenuOpen = !this.isMobileRadialMenuOpen;
  }

  onMobileRadialMenuItemClick(item: any, event: Event): void {
    event.stopPropagation();
    this.isMobileRadialMenuOpen = false;

    if (item.queryParams) {
      this.router.navigate([item.route], { queryParams: item.queryParams });
    } else {
      this.router.navigate([item.route]);
    }
  }

  onMobileRadialMenuClose(event: Event): void {
    event.stopPropagation();
    this.isMobileRadialMenuOpen = false;
  }

  // Close mobile radial menu when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (this.isMobileRadialMenuOpen) {
      const target = event.target as HTMLElement;
      if (!target.closest('.mobile_quick_add') && !target.closest('.mobile_radial_menu')) {
        this.isMobileRadialMenuOpen = false;
      }
    }
    
    // Close popup dropdowns when clicking outside
    const target = event.target as HTMLElement;
    if (!target.closest('.popup-dropdown-container')) {
      // Close all open dropdowns
      this.popupDropdownOpen.set({});
    }
  }

  // Keyboard navigation support for mobile radial menu
  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (this.isMobileRadialMenuOpen) {
      switch (event.key) {
        case 'Escape':
          this.isMobileRadialMenuOpen = false;
          break;
        case '1':
        case '2':
        case '3':
        case '4':
        case '5':
          const index = parseInt(event.key) - 1;
          if (this.mobileRadialMenuItems[index]) {
            this.onMobileRadialMenuItemClick(this.mobileRadialMenuItems[index], event);
          }
          break;
      }
    }
  }

  // Notification popup methods
  markAsRead(notification: Notification, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.notificationService.markAsRead(notification.id);
    // Close the popup after marking as read
    this.notificationService.dismissPopup(notification.id);
  }

  dismissPopup(notificationId: string, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.notificationService.dismissPopup(notificationId);
  }

  pausePopupAutoDismiss(notificationId: string): void {
    this.notificationService.pausePopupAutoDismiss(notificationId);
  }

  resumePopupAutoDismiss(notificationId: string): void {
    this.notificationService.resumePopupAutoDismiss(notificationId);
  }

  onPopupClick(notification: Notification, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    
    // Mark as read first
    this.notificationService.markAsRead(notification.id);
    
    // Get route based on notification object details
    const route = this.notificationService.getNotificationRoute(notification);
    
    if (route) {
      // Get fragment for scrolling to specific update/reply
      const fragment = this.notificationService.getNotificationFragment(notification);
      
      // Navigate with fragment if available
      if (fragment) {
        this.router.navigate(route, { fragment });
      } else {
        this.router.navigate(route);
      }
    }
    
    // Dismiss popup after navigation
    this.notificationService.dismissPopup(notification.id);
  }

  togglePin(notification: Notification) {
    this.notificationService.togglePin(notification.id);
  }

  onCommentClick(notification: Notification, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    
    // Mark as read first
    this.notificationService.markAsRead(notification.id);
    
    // Navigate to the corresponding task/project
    // You can customize this routing based on your notification structure
    if (notification.category === 'timesheet') {
      this.router.navigate(['/timesheets'], { 
        queryParams: { 
          taskId: notification.id,
          comment: 'true' 
        } 
      });
    } else {
      // Default to projects or general notifications
      this.router.navigate(['/projects/projects-all'], { 
        queryParams: { 
          taskId: notification.id,
          comment: 'true' 
        } 
      });
    }
    
    // Close the popup after navigation
    this.notificationService.dismissPopup(notification.id);
  }

  // Popup dropdown methods
  togglePopupDropdown(notificationId: string, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    
    const currentState = this.popupDropdownOpen();
    const newState = { ...currentState };
    newState[notificationId] = !newState[notificationId];
    this.popupDropdownOpen.set(newState);
  }

  isPopupDropdownOpen(notificationId: string): boolean {
    return this.popupDropdownOpen()[notificationId] || false;
  }

  closePopupDropdown(notificationId: string) {
    const currentState = this.popupDropdownOpen();
    const newState = { ...currentState };
    newState[notificationId] = false;
    this.popupDropdownOpen.set(newState);
  }

  onPopupDropdownItemClick(notification: Notification, action: string, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    
    // Close dropdown first
    this.closePopupDropdown(notification.id);
    
    switch (action) {
      case 'mark-read':
        this.markAsRead(notification);
        break;
      case 'pin':
        this.togglePin(notification);
        break;
      case 'comment':
        this.onCommentClick(notification);
        break;
      case 'dismiss':
        this.dismissPopup(notification.id);
        break;
      case 'view-details':
        // Navigate to notification details or task details
        this.router.navigate(['/notifications'], { 
          queryParams: { 
            notificationId: notification.id 
          } 
        });
        this.dismissPopup(notification.id);
        break;
    }
  }


  // Handle image loading errors
  onImageError(notificationId: string): void {
    this.imageLoadError[notificationId] = true;
  }

  // Handle successful image loading
  onImageLoad(notificationId: string): void {
    this.imageLoadError[notificationId] = false;
  }

  // Get initials from name for avatar fallback
  getInitials(name: string): string {
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  }

  /** Human-readable notification object type for popup (task, request, …). */
  getPopupContextTypeLabel(popup: Notification): string {
    const raw = String(popup.contextType || popup.objCode || '').trim().toLowerCase();
    if (!raw || raw === 'general') {
      return '';
    }
    return raw;
  }

  /** Clean action sentence without project/type mashed into it. */
  private getPopupActionText(popup: Notification): string {
    let text = String(popup.titleData?.baseTitle || popup.message || popup.title || '').trim();
    if (!text) {
      return '';
    }

    const type = String(popup.contextType || popup.objCode || '').trim();
    if (type) {
      text = text.replace(new RegExp(`\\s+on\\s+${type}\\s*$`, 'i'), '').trim();
    }

    const projectCandidates = [
      popup.contextProjectName,
      popup.titleData?.projectPrefix,
    ];
    for (const candidate of projectCandidates) {
      const project = String(candidate || '')
        .replace(/\s+in\s*$/i, '')
        .trim();
      if (!project) {
        continue;
      }
      const escaped = project.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      text = text
        .replace(new RegExp(`^${escaped}\\s+in\\s+`, 'i'), '')
        .replace(new RegExp(`^${escaped}\\s+-\\s+`, 'i'), '')
        .trim();
    }

    return text;
  }

  /** Precompute all popup display fields when the popup list signal changes. */
  private buildPopupView(popup: PopupNotification) {
    const objectName = this.getPopupObjectName(popup);
    const messagePrefix = this.getPopupMessagePrefix(popup);
    const dateTime = this.getPopupDateTime(popup);
    const projectLine = this.getPopupProjectLine(popup);
    const clientLine = this.getPopupClientLine(popup);
    const serviceLine = this.getPopupServiceLine(popup);
    const route = this.notificationService.getNotificationRoute(popup);
    const canOpenObject = route != null;
    let objectRouterLink: any[] | null = null;
    let objectQueryParams: Record<string, unknown> | null = null;
    if (canOpenObject && route) {
      if (route.length === 2 && typeof route[1] === 'object') {
        objectRouterLink = [route[0]];
        objectQueryParams = route[1] as Record<string, unknown>;
      } else {
        objectRouterLink = route;
      }
    }

    return {
      popup,
      messagePrefix,
      objectName,
      readableMessage: `${messagePrefix || ''}${objectName || ''}`.trim() || this.getPopupReadableMessage(popup),
      canOpenObject,
      objectRouterLink,
      objectQueryParams,
      objectFragment: this.notificationService.getNotificationFragment(popup) ?? undefined,
      dateTime,
      projectLine,
      projectRoute: this.getPopupProjectRoute(popup),
      clientLine,
      serviceLine,
      hasMeta: !!(dateTime || projectLine || clientLine || serviceLine),
      initials: this.getInitials(popup.user.name),
    };
  }

  /** Primary object title (request subject / project / task name). */
  private getPopupObjectName(popup: Notification): string {
    const candidates = [
      popup.contextObjectName,
      popup.objectname,
      popup.objectDetails?.requestName,
      popup.objectDetails?.taskName,
      popup.objectDetails?.issueName,
      popup.objectDetails?.revisionName,
      popup.objectDetails?.projectName,
      popup.objectDetails?.name,
    ];
    const type = String(popup.contextType || popup.objCode || '').trim().toUpperCase();
    for (const value of candidates) {
      const text = String(value || '').trim();
      if (!text) {
        continue;
      }
      if (type && text.toUpperCase() === type) {
        continue;
      }
      return this.capitalizeFirst(text);
    }
    return '';
  }

  /** Capitalize first letter for popup display (keeps rest of string as stored). */
  private capitalizeFirst(value: string): string {
    const text = String(value || '').trim();
    if (!text) {
      return '';
    }
    return text.charAt(0).toLocaleUpperCase() + text.slice(1);
  }

  /**
   * Readable summary, e.g. "Added an update in the task: test task 1"
   */
  private getPopupReadableMessage(popup: Notification): string {
    const action = this.getPopupActionText(popup);
    const typeLabel = this.getPopupContextTypeLabel(popup);
    const objectName = this.getPopupObjectName(popup);

    if (action && typeLabel && objectName) {
      return `${action}  ${objectName}`;
    }
    if (action && objectName) {
      return `${action} ${objectName}`;
    }
    if (action && typeLabel) {
      return `${action}  ${typeLabel}`;
    }
    return action || objectName || '';
  }

  /** Text before the object name (object name is rendered as a link when possible). */
  private getPopupMessagePrefix(popup: Notification): string {
    const action = this.getPopupActionText(popup);
    const typeLabel = this.getPopupContextTypeLabel(popup);
    const objectName = this.getPopupObjectName(popup);

    if (action && typeLabel && objectName) {
      return `${action}   `;
    }
    if (action && objectName) {
      return `${action} `;
    }
    if (action && typeLabel) {
      return `${action} `;
    }
    return objectName ? '' : action;
  }

  private getPopupProjectRoute(popup: Notification): string[] | null {
    const projectId = String(popup.contextProjectId || '').trim();
    if (!projectId) {
      return null;
    }
    // Avoid a second identical link when the popup object itself is the project.
    const type = String(popup.contextType || popup.objCode || '').toUpperCase();
    if (type === 'PROJECT') {
      return null;
    }
    return ['/projects', 'project-details', projectId];
  }

  onPopupObjectLinkClick(popup: Notification, event: Event): void {
    event.stopPropagation();
    this.notificationService.markAsRead(popup.id);
    this.notificationService.dismissPopup(popup.id);
  }

  private getPopupDateTime(popup: Notification): string {
    const raw = popup.createdAt || popup.timestamp || '';
    if (!raw || String(raw).trim().toLowerCase() === 'recently') {
      return '';
    }

    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) {
      // Already a display string from the service (e.g. "2 hours ago") — show as-is.
      return String(raw).trim();
    }

    return this.popupDateTimeFormatter.format(date);
  }

  private getPopupProjectLine(popup: Notification): string {
    const objectName = this.getPopupObjectName(popup).toLowerCase();
    const project = String(popup.contextProjectName || '').trim();
    if (!project || project.toLowerCase() === objectName) {
      return '';
    }
    // For PROJECT notifications the object name already is the project
    if (String(popup.contextType || popup.objCode || '').toUpperCase() === 'PROJECT') {
      return '';
    }
    return this.capitalizeFirst(project);
  }

  private getPopupClientLine(popup: Notification): string {
    const objectName = this.getPopupObjectName(popup).toLowerCase();
    const project = String(popup.contextProjectName || '').toLowerCase();
    const client = String(popup.contextClientName || '').trim();
    if (!client || client.toLowerCase() === objectName || client.toLowerCase() === project) {
      return '';
    }
    return this.capitalizeFirst(client);
  }

  private getPopupServiceLine(popup: Notification): string {
    const objectName = this.getPopupObjectName(popup).toLowerCase();
    const project = String(popup.contextProjectName || '').toLowerCase();
    const client = String(popup.contextClientName || '').toLowerCase();
    const service = String(popup.contextServiceName || '').trim();
    if (
      !service ||
      service.toLowerCase() === objectName ||
      service.toLowerCase() === project ||
      service.toLowerCase() === client
    ) {
      return '';
    }
    return this.capitalizeFirst(service);
  }
}
